import React, { useContext, useEffect, useState } from 'react';
import SubmitButton from '../../components/SubmitButton';
import SmallButton from '../../components/SmallButton';
import {
  RouteComponentProps,
  useParams,
  withRouter,
  StaticContext,
} from 'react-router';
import { Order, OrderResponse } from '../../models/APIModels';
import Moment from 'moment';
import Title from '../../components/Title';
import Label from '../../components/Label';
import { axiosKK } from '../../services/networkRequest';
import FileDownload from 'js-file-download';
import Barcode from '../../components/Barcode';
import StatusBlock from '../../components/StatusBlock';
import PaymentBlock from '../../components/PaymentBlock';
import PaymentStatusBlock from '../../components/PaymentStatusBlock';
import { Role } from '../../utilities/Role';
import UserContext from '../../contexts/UserContext';
import { NoteBlock } from '../../components/NoteBlock';
import { OrderBoxesDisplay } from '../User/Order';
import DPDShippingModal from '../../components/DPDShippingModal';

interface OrderParams {
  id: string;
}

interface OrderComponentState {
  order?: Order;
}

function AdminOrderComponent({
  history,
}: RouteComponentProps<
  undefined,
  StaticContext,
  OrderComponentState
>): JSX.Element {
  const { id } = useParams<OrderParams>();
  const [order, setOrder] = useState(history.location?.state?.order);
  const [showONSModal, setShowONSModal] = useState(false);
  const statuses = order?.statuses || [];
  const customer = order?.customer;
  const payments = order?.payments || [];
  const notes = order?.notes || [];
  const orderBoxes = order?.orderBoxes || [];
  const { user: loggedInUser } = useContext(UserContext);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!order) {
      // Try to fetch as regular order first
      axiosKK.get<OrderResponse>(`/order/${id}`)
        .then(({ data }) => {
          setOrder(data.order);
        })
        .catch(() => {
          // If that fails, try to fetch as removal order
          axiosKK.get<OrderResponse>(`/admin/removals/orders/${id}`)
            .then(({ data }) => {
              setOrder(data.order);
            })
            .catch((error) => {
              console.error('Error fetching order:', error);
            });
        });
    }
  }, [order, id]);

  return (
    <>
      <section className="nav-status-bar">
        <div className="status-bar-content">
          <i className="fas fa-arrow-circle-left" />
          <SmallButton
            className="submitButton"
            onClick={() => {
              history.push('/admin/dashboard');
            }}
          >
            Back
          </SmallButton>
        </div>
      </section>
      <Title>{`${
        order?.orderType === 'removal'
          ? 'Removal'
          : order?.suitcaseOrder
            ? 'Suitcase'
            : order?.storageRequired
              ? 'Storage'
              : 'Shipping'
      } Order Overview`}</Title>
      <section className="print-doc-and-items-block">
        <div className="print-doc admin-print-doc">
          <div className="top-border">Basic Info</div>
          <div className="second-border">
            <p className="roomPack">
              {order?.roomPackIncluded ? 'Fulfilled' : 'Not fulfilled'}
            </p>
            <p className="date">
              Order Created At:{' '}
              {Moment(order?.createdAt).format('dddd, MMM Do YYYY')}
            </p>
          </div>
          <div className="basic-block">
            <div className="id-city-requirement">
              <div className="id-and-city">
                <p className="id">{order?.id}</p>
                <p className="city">{order?.orderType === 'removal' ? order?.collectionCity : order?.city}</p>
                {customer && (
                  <div
                    onClick={() =>
                      history.push(`/admin/customer/${customer.id}`)
                    }
                    className="name"
                  >
                    <p>
                      {customer?.firstName} {customer?.lastName}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="addresses-block">
              <div className="each">
                <Label>{order?.orderType === 'removal' ? 'Move Date & Collection Address:' : 'Collection Address:'}</Label>
                <p>{order?.collectionAddress}</p>
                <p>
                  {order?.orderType === 'removal' 
                    ? order?.moveDate && Moment(order.moveDate).format('dddd, MMM Do YYYY')
                    : order?.collectionDate && Moment(order.collectionDate).format('dddd, MMM Do YYYY')}
                </p>
              </div>
              <div className="each">
                <Label>Delivery Address:</Label>
                <p>{order?.deliveryAddress}</p>
                <p>
                  {order?.deliveryDate &&
                    Moment(order.deliveryDate).format('dddd, MMM Do YYYY')}
                </p>
              </div>
            </div>
          </div>

          {order?.orderType === 'removal' ? (
            <div className="customer-print-doc">
              <div className="items-block">
                <div className="eachBox">
                  <div className="border-top" />
                  <div className="content">
                    <div className="basic">
                      <h3>Removal Service</h3>
                      <p><strong>Tier:</strong> {order?.tierName}</p>
                    </div>
                    <div className="cost">
                      <Label htmlFor={`removal-details-${order?.id}`}>Property Details:</Label>
                      <div className="costDetails">
                        <Label htmlFor={`removal-floor-${order?.id}`}>Property Floor:</Label>
                        <p id={`removal-floor-${order?.id}`}>
                          {order?.propertyFloor === '0' ? 'Ground Floor' : `Floor ${order?.propertyFloor}`}
                        </p>
                        {order?.propertyHasLift !== null && (
                          <>
                            <Label htmlFor={`removal-lift-${order?.id}`}>Lift Available:</Label>
                            <p id={`removal-lift-${order?.id}`}>{order?.propertyHasLift ? 'Yes' : 'No'}</p>
                          </>
                        )}
                        <Label htmlFor={`removal-furniture-${order?.id}`}>Furniture:</Label>
                        <p id={`removal-furniture-${order?.id}`}>{order?.propertyHasFurniture ? 'Yes' : 'No'}</p>
                        <Label htmlFor={`removal-parking-${order?.id}`}>Parking Available:</Label>
                        <p id={`removal-parking-${order?.id}`}>{order?.propertyHasParking ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                    {(order?.packingMaterialsId || order?.storageId) && (
                      <div className="cost">
                        <Label htmlFor={`removal-extras-${order?.id}`}>Extras:</Label>
                        <div className="costDetails">
                          {order?.packingMaterialsId && (
                            <>
                              <Label htmlFor={`removal-packing-${order?.id}`}>Packing Materials:</Label>
                              <p id={`removal-packing-${order?.id}`}>Included</p>
                            </>
                          )}
                          {order?.storageId && (
                            <>
                              <Label htmlFor={`removal-storage-${order?.id}`}>Storage:</Label>
                              <p id={`removal-storage-${order?.id}`}>{order?.storageSize}</p>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : order?.suitcaseOrder && (
            <div className="customer-print-doc">
              <div className="items-block">
                {orderBoxes.map((orderBox, index) => {
                  return (
                    <OrderBoxesDisplay
                      key={index}
                      id={orderBox.id}
                      suitcaseOrder={order?.suitcaseOrder}
                      barcode={orderBox?.barcode}
                      orderBox={orderBox}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <NoteBlock notes={notes} />
          <StatusBlock statuses={statuses} />
          <PaymentStatusBlock payments={payments} />
          {loggedInUser?.role === Role.Admin && (
            <PaymentBlock
              initialCost={order?.orderType === 'removal' ? order?.totalInitialCost : order?.initialCost}
              monthlyCost={order?.orderType === 'removal' ? order?.monthlyStorageCost : order?.monthlyCost}
              storageRequired={order?.orderType === 'removal' ? !!order?.storageId : order?.storageRequired}
            />
          )}

          <span />
        </div>
        {!order?.suitcaseOrder && order?.orderType !== 'removal' && (
          <div className="print-doc admin-print-doc barcode-doc">
            <div className="top-border">Barcodes</div>
            <div className="items-block">
              <div className="table">
                <div className="thead">
                  <div className="tr">
                    <div className="td img">Image</div>
                    <div className="td name">Item Name</div>
                    <div className="td code">Barcode</div>
                  </div>
                </div>
                <div className="tbody">
                  {orderBoxes.map((orderBox) => {
                    const orderBoxDetail = orderBox?.box;
                    return (
                      <div key={orderBox.id} className="tr">
                        <div className="td img">
                          <img src={orderBoxDetail?.imageUrl} alt="" />
                        </div>
                        <div className="td name">{orderBoxDetail?.name}</div>
                        <div className="td code">
                          <Barcode>{orderBox?.barcode}</Barcode>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <div className="btn-row">
        {loggedInUser?.role === Role.Admin && order?.orderType !== 'removal' && (
          <SubmitButton
            red={true}
            onClick={() => history.push(`/admin/order/${id}/edit`)}
          >
            Edit
          </SubmitButton>
        )}
        {order?.orderType !== 'removal' && (
          <SubmitButton
            disabled={!order}
            onClick={() => {
              axiosKK
                .get(`/order/${order?.id}/label`, {
                  responseType: 'blob',
                })
                .then((response) => {
                  FileDownload(response.data, `order-${id}-labels.pdf`);
                });
            }}
          >
            Download
          </SubmitButton>
        )}
        {loggedInUser?.role === Role.Admin && order?.orderType !== 'removal' && (
          <SubmitButton onClick={() => setShowONSModal(true)}>
            Create DPD Label
          </SubmitButton>
        )}
      </div>

      {order && showONSModal && (
        <DPDShippingModal
          isOpen={showONSModal}
          close={() => setShowONSModal(false)}
          collectionAddress={order.collectionAddress || ''}
          deliveryAddress={order.deliveryAddress || ''}
          orderBoxes={order.orderBoxes}
          orderId={order.id}
          customer={customer}
        />
      )}
    </>
  );
}

export default withRouter(AdminOrderComponent);
